import type { LookLike, LookLikeInference } from '@famecreator/shared-types/look-like';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import type { PersonaData as Persona } from '@/shared/api/persona-service';

export interface ThreeDSData {
  paymentAmount: number;
  creditAmount: number;
  clientSecret: string;
}

interface ModalState {
  // Look-like detail modal
  currentLookLike: LookLike | null;
  isLoadingLookLike: boolean;

  // Persona detail modal
  currentPersona: Persona | null;
  isLoadingPersona: boolean;

  // 3DS authentication modal
  threeDSData: {
    paymentAmount: number;
    creditAmount: number;
    clientSecret: string;
  } | null;
  isProcessing3DS: boolean;

  // Generic modal state
  openModals: Set<string>;
  modalData: Record<string, unknown>;

  // Other modal states can be added here
  // e.g., imageViewerModal, confirmationModal, etc.
}

interface ModalActions {
  // Look-like modal management
  setCurrentLookLike: (lookLike: LookLike | null) => void;
  setLoadingLookLike: (loading: boolean) => void;
  updateCurrentLookLike: (updates: Partial<LookLike>) => void;
  closeLookLikeModal: () => void;

  // Persona modal management
  setCurrentPersona: (persona: Persona | null) => void;
  setLoadingPersona: (loading: boolean) => void;
  updateCurrentPersona: (updates: Partial<Persona>) => void;
  closePersonaModal: () => void;

  // 3DS modal management
  openThreeDSModal: (data: { paymentAmount: number; creditAmount: number; clientSecret: string }) => void;
  closeThreeDSModal: () => void;
  setProcessing3DS: (processing: boolean) => void;

  // Generic modal management
  isModalOpen: (modalId: string) => boolean;
  openModal: (modalId: string, data?: Record<string, unknown>) => void;
  closeModal: (modalId: string) => void;
  getModalData: (modalId: string) => Record<string, unknown> | undefined;

  // Inference-specific updates for current look-like
  updateLookLikeInference: (inferenceId: number, updates: Partial<LookLikeInference>) => void;

  // Reset all modals
  resetAll: () => void;
}

type ModalStore = ModalState & ModalActions;

const initialState: ModalState = {
  currentLookLike: null,
  isLoadingLookLike: false,
  currentPersona: null,
  isLoadingPersona: false,
  threeDSData: null,
  isProcessing3DS: false,
  openModals: new Set<string>(),
  modalData: {},
};

export const useModalStore = create<ModalStore>()(
  subscribeWithSelector((set) => ({
    ...initialState,

    // Look-like modal management
    setCurrentLookLike: (lookLike) => set({ currentLookLike: lookLike }),

    setLoadingLookLike: (loading) => set({ isLoadingLookLike: loading }),

    updateCurrentLookLike: (updates) =>
      set((state) => ({
        currentLookLike: state.currentLookLike ? { ...state.currentLookLike, ...updates } : null,
      })),

    closeLookLikeModal: () =>
      set({
        currentLookLike: null,
        isLoadingLookLike: false,
      }),

    // Persona modal management
    setCurrentPersona: (persona) => set({ currentPersona: persona }),

    setLoadingPersona: (loading) => set({ isLoadingPersona: loading }),

    updateCurrentPersona: (updates) =>
      set((state) => ({
        currentPersona: state.currentPersona ? { ...state.currentPersona, ...updates } : null,
      })),

    closePersonaModal: () =>
      set({
        currentPersona: null,
        isLoadingPersona: false,
      }),

    // 3DS modal management
    openThreeDSModal: (data: ThreeDSData): void =>
      set({
        threeDSData: data,
        openModals: new Set([...useModalStore.getState().openModals, 'threeDS']),
      }),

    closeThreeDSModal: () =>
      set((state) => {
        const newOpenModals = new Set(state.openModals);
        newOpenModals.delete('threeDS');
        return {
          threeDSData: null,
          isProcessing3DS: false,
          openModals: newOpenModals,
        };
      }),

    setProcessing3DS: (processing: boolean): void => set({ isProcessing3DS: processing }),

    // Generic modal management
    isModalOpen: (modalId: string): boolean => {
      const state = useModalStore.getState();
      return state.openModals.has(modalId);
    },

    openModal: (modalId: string, data?: Record<string, unknown>) =>
      set((state) => ({
        openModals: new Set([...state.openModals, modalId]),
        modalData: data ? { ...state.modalData, [modalId]: data } : state.modalData,
      })),

    closeModal: (modalId: string) =>
      set((state) => {
        const newOpenModals = new Set(state.openModals);
        newOpenModals.delete(modalId);
        const newModalData = { ...state.modalData };
        delete newModalData[modalId];
        return {
          openModals: newOpenModals,
          modalData: newModalData,
        };
      }),

    getModalData: (modalId: string): Record<string, unknown> | undefined => {
      const state = useModalStore.getState();
      return state.modalData[modalId] as Record<string, unknown> | undefined;
    },

    // Inference-specific updates for current look-like
    updateLookLikeInference: (inferenceId: number, updates: Partial<LookLikeInference>): void => {
      set((state) => {
        if (!state.currentLookLike) {
          return state;
        }

        const updatedInferences = state.currentLookLike.inferences.map((inference) =>
          inference.id === inferenceId ? { ...inference, ...updates } : inference,
        );

        return {
          currentLookLike: {
            ...state.currentLookLike,
            inferences: updatedInferences,
          },
        };
      });
    },

    // Reset all modals
    resetAll: () => set(initialState),
  })),
);
